import {
  AdminPanelSettings,
  Apps,
  Assessment,
  Assignment,
  ChevronLeft,
  ChevronRight,
  Close,
  Event,
  FindInPage,
  Home,
  Menu,
  People,
  Person,
  Security,
  Work,
} from "@mui/icons-material";
import { Box, Paper } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { makeStyles } from "@mui/styles";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";

import { useLocation } from "react-router-dom";
import { MenuCategory } from "../../Enums/MenuCategory";
import { Rights } from "../../Enums/Rights";
import { Roles } from "../../Enums/Roles";
import { RouteTypes } from "../../Enums/RouteTypes";
import { SubMenuRoutes } from "../../Enums/SubMenuRoutes";
import ProfileDropdown from "../ProfileComponent/ProfileDropdown";
import NavBarItem from "./NavBarItem";
import SubMenu from "./SubMenu/SubMenu";
import SubMenuItem from "./SubMenu/SubMenuItem";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",

    "& .MuiList-root": {
      overflowY: "auto",
      overflowX: "visible",
    },
  },
  paper: {
    background: theme.palette.primary.main,
    overflowX: "hidden",
    zIndex: 10,
    height: "100%",
    alignItems: "center",
    boxShadow: "0px 0px 5px darkgrey",
    display: "block",
    flexDirection: "row",
  },
  paperHidden: {
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
    background: theme.palette.primary.main,
    overflowX: "hidden",
    zIndex: 10,
    height: "100vh",
    alignItems: "center",
    boxShadow: "0px 0px 5px darkgrey",
    display: "block",
  },
  exitButton: {
    [theme.breakpoints.down("md")]: {
      display: "block",
      padding: "20px",
      justifyContent: "center",
    },
    display: "none",
  },
  bottomButtons: {
    position: "absolute",
    bottom: "0px",
  },
  navBarArrow: {
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
    position: "absolute",
    top: "50vh",
    zIndex: "20",
    left: "75px",
  },
  subMenuArrow: {
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
    position: "absolute",
    top: "50vh",
    right: "-20px",
  },
  arrowButton: {
    "&:hover": {
      backgroundColor: "white",
      color: "#81c784",
    },
    transition: "0.5s",
    height: "40px",
    width: "40px",
    background: "white",
    boxShadow: "0px 0px 5px darkgrey",
  },
  navHamburger: {
    [theme.breakpoints.down("md")]: {
      position: "absolute",
      left: "20px",
      top: "20px",
      zIndex: "10",
      display: "block",
    },
    display: "none",
  },
  itemIcon: {
    minWidth: "56px",
    color: "white",
    justifyContent: "center",
  },
}));

export default function NavBar() {
  const classes = useStyles();
  const location = useLocation();

  const [selectedNavBarIndex, setSelectedNavBarIndex] = useState(0);
  const [navBarVisible, setNavBarVisible] = useState(true);
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [isAdminPage, setIsAdminPage] = useState(false);

  // Initialize EnableABACApplicationManagement LD flag
  // TODO: Remove with ABAC-32
  const { enableAbacApplicationManagement } = useFlags();

  function updateSelectedNoSubMenu(index) {
    if (index !== selectedNavBarIndex) {
      setSubMenuOpen(false);
      setSelectedNavBarIndex(index);
    }
  }

  function openSubMenu(index) {
    setSelectedNavBarIndex(index);
    if (index === selectedNavBarIndex || !subMenuOpen) {
      setSubMenuOpen(!subMenuOpen);
    }
  }

  function toggleNavBar() {
    setNavBarVisible(!navBarVisible);
    if (navBarVisible && subMenuOpen) {
      setSubMenuOpen(false);
    }
  }

  useEffect(() => {
    setIsAdminPage(location.pathname.includes("/admin"));
  }, [location.pathname]);

  return (
    <>
      {!isAdminPage ? (
        <ProfileDropdown />
      ) : (
        <AdminPanelSettings
          sx={{
            width: "80px",
            height: "80px",
            position: "absolute",
            top: "2.25vh",
            right: "3%",
            zIndex: "5",
            color: "NAS.blue",
          }}
        />
      )}
      <div className={classes.root}>
        <Paper
          square
          sx={
            navBarVisible
              ? {
                  backgroundColor: !isAdminPage ? "primary.main" : "NAS.blue",
                  overflowX: "hidden",
                  zIndex: 10,
                  height: "100vh",
                  alignItems: "center",
                  boxShadow: "0px 0px 5px darkgrey",
                  display: "block",
                  flexDirection: "row",
                }
              : {
                  display: { xs: "none", sm: "none", md: "block" },
                  backgroundColor: !isAdminPage ? "primary.main" : "NAS.blue",
                  overflowX: "hidden",
                  zIndex: 10,
                  height: "100vh",
                  alignItems: "center",
                  boxShadow: "0px 0px 5px darkgrey",
                }
          }
        >
          {navBarVisible ? (
            <Box
              sx={{
                display: { sm: "block", md: "none" },
              }}
            >
              <NavBarItem
                icon={<Close />}
                route={null}
                routeType={RouteTypes.Unprotected}
                clickEvent={() => toggleNavBar()}
                selectedNavBarIndex={selectedNavBarIndex}
                setSelectedNavBarIndex={setSelectedNavBarIndex}
                routes={[]}
              />
            </Box>
          ) : null}
          <NavBarItem
            icon={<Home />}
            clickEvent={() => updateSelectedNoSubMenu(0)}
            menuIndex={MenuCategory.Dashboard}
            selectedNavBarIndex={selectedNavBarIndex}
            setSelectedNavBarIndex={setSelectedNavBarIndex}
            tooltipText={"Dashboard"}
            route={"/"}
            routeType={RouteTypes.Unprotected}
            routes={[]}
          />
          <NavBarItem
            icon={<People />}
            clickEvent={() => openSubMenu(MenuCategory.UserManagement)}
            menuIndex={MenuCategory.UserManagement}
            selectedNavBarIndex={selectedNavBarIndex}
            setSelectedNavBarIndex={setSelectedNavBarIndex}
            tooltipText={"User Management"}
            route={null}
            routeType={RouteTypes.ClientCrmRightsProtected}
            requiredPermissions={Rights.Users}
            routes={SubMenuRoutes.UserManagement}
          />
          <NavBarItem
            icon={<Assignment />}
            clickEvent={() => openSubMenu(MenuCategory.ClientConfiguration)}
            menuIndex={MenuCategory.ClientConfiguration}
            selectedNavBarIndex={selectedNavBarIndex}
            setSelectedNavBarIndex={setSelectedNavBarIndex}
            tooltipText={"Client Configuration"}
            route={null}
            routeType={RouteTypes.ClientRightsProtected}
            requiredPermissions={[Rights.Configurations, Rights.PrefilteredLinks, Rights.Users]}
            routes={SubMenuRoutes.ClientConfiguration}
          />
          <NavBarItem
            icon={<FindInPage />}
            clickEvent={() => openSubMenu(MenuCategory.Search)}
            menuIndex={MenuCategory.Search}
            selectedNavBarIndex={selectedNavBarIndex}
            setSelectedNavBarIndex={setSelectedNavBarIndex}
            tooltipText={"Search"}
            route={null}
            routeType={RouteTypes.ClientRightsProtected}
            requiredPermissions={[Rights.Configurations]}
            routes={SubMenuRoutes.Search}
          />
          <NavBarItem
            icon={<Work />}
            clickEvent={() => openSubMenu(MenuCategory.Jobs)}
            menuIndex={MenuCategory.Jobs}
            selectedNavBarIndex={selectedNavBarIndex}
            setSelectedNavBarIndex={setSelectedNavBarIndex}
            tooltipText={"Jobs"}
            route={null}
            routeType={RouteTypes.ClientRightsProtected}
            requiredPermissions={[Rights.Configurations, Rights.FeaturedJobs, Rights.Locations]}
            routes={SubMenuRoutes.Jobs}
          />
          <NavBarItem
            icon={<Event />}
            clickEvent={() => openSubMenu(MenuCategory.Events)}
            menuIndex={MenuCategory.Events}
            selectedNavBarIndex={selectedNavBarIndex}
            setSelectedNavBarIndex={setSelectedNavBarIndex}
            tooltipText={"Events"}
            route={null}
            routeType={RouteTypes.ClientRightsProtected}
            requiredPermissions={[Rights.Events, Rights.EventVenues, Rights.EventSponsors, Rights.EventTypes]}
            routes={SubMenuRoutes.Events}
          />
          <NavBarItem
            icon={<Assessment />}
            clickEvent={() => openSubMenu(MenuCategory.Reports)}
            menuIndex={MenuCategory.Reports}
            selectedNavBarIndex={selectedNavBarIndex}
            setSelectedNavBarIndex={setSelectedNavBarIndex}
            tooltipText={"Reports"}
            route={null}
            routeType={RouteTypes.ClientRightsProtected}
            requiredPermissions={[Rights.TalentNetworkData]}
            routes={SubMenuRoutes.Reports}
          />
          <NavBarItem
            icon={<Security />}
            clickEvent={() => openSubMenu(MenuCategory.ABAC)}
            menuIndex={MenuCategory.ABAC}
            selectedNavBarIndex={selectedNavBarIndex}
            setSelectedNavBarIndex={setSelectedNavBarIndex}
            tooltipText={"ABAC"}
            route={null}
            routeType={RouteTypes.ClientRoleProtected}
            requiredPermissions={Roles.SuperAdmin}
            routes={SubMenuRoutes.ABAC}
          />
          <div className={classes.bottomButtons}>
            {enableAbacApplicationManagement && (
              <NavBarItem
                icon={<AdminPanelSettings />}
                clickEvent={() => openSubMenu(MenuCategory.CrossClient)}
                menuIndex={MenuCategory.CrossClient}
                selectedNavBarIndex={selectedNavBarIndex}
                setSelectedNavBarIndex={setSelectedNavBarIndex}
                tooltipText={"Cross Client Management"}
                route={null}
                routeType={RouteTypes.ClientRoleProtected}
                requiredPermissions={Roles.SuperAdmin}
                routes={[]}
              />
            )}
            <NavBarItem
              icon={<Apps />}
              clickEvent={() => openSubMenu(MenuCategory.MoreApps)}
              menuIndex={MenuCategory.MoreApps}
              selectedNavBarIndex={selectedNavBarIndex}
              setSelectedNavBarIndex={setSelectedNavBarIndex}
              tooltipText={"More Apps"}
              route={null}
              routeType={RouteTypes.Unprotected}
              routes={[]}
            />
            <NavBarItem
              icon={<Person />}
              clickEvent={() => updateSelectedNoSubMenu(MenuCategory.Profile)}
              menuIndex={MenuCategory.Profile}
              selectedNavBarIndex={selectedNavBarIndex}
              setSelectedNavBarIndex={setSelectedNavBarIndex}
              tooltipText={"Profile"}
              route={"/profile"}
              routeType={RouteTypes.Unprotected}
              routes={SubMenuRoutes.Profile}
            />
          </div>
        </Paper>

        <SubMenu
          open={subMenuOpen}
          selected={selectedNavBarIndex}
          index={MenuCategory.UserManagement}
          routeType={RouteTypes.ClientCrmRightsProtected}
          requiredPermissions={Rights.Users}
        >
          <SubMenuItem
            title={"User Management"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.UserManagement}
            route={"/users"}
            routeType={RouteTypes.ClientCrmRightsProtected}
            requiredPermissions={Rights.Users}
          />
        </SubMenu>

        <SubMenu
          open={subMenuOpen}
          selected={selectedNavBarIndex}
          index={MenuCategory.ClientConfiguration}
          routeType={RouteTypes.ClientRightsProtected}
          requiredPermissions={[Rights.Configurations, Rights.PrefilteredLinks, Rights.Users]}
        >
          <SubMenuItem
            title={"Display"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.ClientConfiguration}
            route={"/display"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Configurations}
          />
          <SubMenuItem
            title={"Email Apply Link"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.ClientConfiguration}
            route={"/emailapplylink"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Configurations}
          />
          <SubMenuItem
            title={"Tracking Pixels"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.ClientConfiguration}
            route={"/trackingpixels"}
            routeType={RouteTypes.ClientRoleProtected}
            requiredPermissions={Roles.SuperAdmin}
          />
          <SubMenuItem
            title={"External Accounts"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.ClientConfiguration}
            route={"/externalaccounts"}
            routeType={RouteTypes.ClientRoleProtected}
            requiredPermissions={Roles.SuperAdmin}
          />
          <SubMenuItem
            title={"Master Cache"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.ClientConfiguration}
            route={"/mastercache"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Configurations}
          />
          <SubMenuItem
            title={"Banner"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.ClientConfiguration}
            route={"/banner"}
            routeType={RouteTypes.ClientRoleProtected}
            requiredPermissions={Roles.SuperAdmin}
          />
          <SubMenuItem
            title={"Job Details Ancillary"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.ClientConfiguration}
            route={"/ancillary"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Configurations}
          />
          <SubMenuItem
            title={"Prefiltered Links"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.ClientConfiguration}
            route={"/prefilteredlinks"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.PrefilteredLinks}
          />
          <SubMenuItem
            icon={<Assignment />}
            title={"Users"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Administration}
            route={"/users"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Users}
          />
        </SubMenu>

        <SubMenu
          open={subMenuOpen}
          selected={selectedNavBarIndex}
          index={MenuCategory.Search}
          routeType={RouteTypes.ClientRightsProtected}
          requiredPermissions={[Rights.Configurations]}
        >
          <SubMenuItem
            title={"Forms"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Search}
            route={"/forms"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Configurations}
          />
          <SubMenuItem
            title={"Event Search"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Search}
            route={"/eventsearch"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Configurations}
          />
          <SubMenuItem
            title={"Map Configuration"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Search}
            route={"/mapconfiguration"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Configurations}
          />
          <SubMenuItem
            title={"Custom Radius"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Search}
            route={"/customradius"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Configurations}
          />
          <SubMenuItem
            title={"Results"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Search}
            route={"/results"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Configurations}
          />
        </SubMenu>

        <SubMenu
          open={subMenuOpen}
          selected={selectedNavBarIndex}
          index={MenuCategory.Jobs}
          routeType={RouteTypes.ClientRightsProtected}
          requiredPermissions={[Rights.Configurations, Rights.FeaturedJobs, Rights.Locations]}
        >
          <SubMenuItem
            title={"Locations"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Jobs}
            route={"/locations"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Locations}
          />
          <SubMenuItem
            title={"Featured Groups"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Jobs}
            route={"/featuredgroups"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.FeaturedJobs}
          />
          <SubMenuItem
            title={"Featured Jobs"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Jobs}
            route={"/featuredjobs"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.FeaturedJobs}
          />
          <SubMenuItem
            title={"Job Groups"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Jobs}
            route={"/jobgroups"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Configurations}
          />
        </SubMenu>

        <SubMenu
          open={subMenuOpen}
          selected={selectedNavBarIndex}
          index={MenuCategory.Events}
          routeType={RouteTypes.ClientRightsProtected}
          requiredPermissions={[Rights.Events, Rights.EventVenues, Rights.EventSponsors, Rights.EventTypes]}
        >
          <SubMenuItem
            title={"Featured Events"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Events}
            route={"/featuredevents"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Events}
          />
          <SubMenuItem
            title={"Event Venues"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Events}
            route={"/eventvenues"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.EventVenues}
          />
          <SubMenuItem
            title={"Event Sponsors"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Events}
            route={"/eventsponsors"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.EventSponsors}
          />
          <SubMenuItem
            title={"Event Types"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Events}
            route={"/eventtypes"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.EventTypes}
          />
          <SubMenuItem
            title={"Events"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Events}
            route={"/events"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.Events}
          />
        </SubMenu>

        <SubMenu
          open={subMenuOpen}
          selected={selectedNavBarIndex}
          index={MenuCategory.Reports}
          routeType={RouteTypes.ClientRightsProtected}
          requiredPermissions={[Rights.TalentNetworkData]}
        >
          <SubMenuItem
            title={"Job Alerts Report"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Reports}
            route={"/jobalertsreport"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.TalentNetworkData}
          />
          <SubMenuItem
            title={"Marketing Report"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.Reports}
            route={"/marketreport"}
            routeType={RouteTypes.ClientRightProtected}
            requiredPermissions={Rights.TalentNetworkData}
          />
        </SubMenu>

        {enableAbacApplicationManagement && (
          <>
            <SubMenu
              open={subMenuOpen}
              selected={selectedNavBarIndex}
              index={MenuCategory.ABAC}
              routeType={RouteTypes.ClientRoleProtected}
              requiredPermissions={Roles.SuperAdmin}
            >
              <SubMenuItem
                title="Application Management"
                navMenuSelection={selectedNavBarIndex}
                menuCategory={MenuCategory.ABAC}
                route="/abac"
                routeType={RouteTypes.ClientRoleProtected}
                requiredPermissions={Roles.SuperAdmin}
              />
              <SubMenuItem
                title="Client Feature Variations"
                navMenuSelection={selectedNavBarIndex}
                menuCategory={MenuCategory.ABAC}
                route="/abacClient"
                routeType={RouteTypes.ClientRoleProtected}
                requiredPermissions={Roles.SuperAdmin}
              />
              <SubMenuItem
                title="Users"
                navMenuSelection={selectedNavBarIndex}
                menuCategory={MenuCategory.ABAC}
                route="/abacUsers"
                routeType={RouteTypes.ClientRoleProtected}
                requiredPermissions={Roles.SuperAdmin}
              />
            </SubMenu>
            <SubMenu
              open={subMenuOpen}
              selected={selectedNavBarIndex}
              index={MenuCategory.CrossClient}
              routeType={RouteTypes.RoleProtected}
              requiredPermissions={Roles.SuperAdmin}
            >
              <SubMenuItem
                title={"User Management"}
                navMenuSelection={selectedNavBarIndex}
                menuCategory={MenuCategory.CrossClient}
                route={"/adminUserManagement"}
                routeType={RouteTypes.RoleProtected}
                requiredPermissions={Roles.SuperAdmin}
              />
            </SubMenu>
          </>
        )}

        <SubMenu
          open={subMenuOpen}
          selected={selectedNavBarIndex}
          index={MenuCategory.MoreApps}
          routeType={RouteTypes.Unprotected}
        >
          <SubMenuItem
            title={"Activate CRM"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.MoreApps}
            route={import.meta.env.VITE_ACTIVATE_CRM}
            routeType={RouteTypes.ClientCrmAccessRightsProtected}
          />
          <SubMenuItem
            title={"NAS Management"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.MoreApps}
            route={import.meta.env.VITE_NAS_PORTAL}
            routeType={RouteTypes.ClientRoleProtected}
            requiredPermissions={Roles.SuperAdmin}
          />
          <SubMenuItem
            title={"Talentegy Analytics"}
            navMenuSelection={selectedNavBarIndex}
            menuCategory={MenuCategory.MoreApps}
            route={import.meta.env.VITE_TALENTEGY}
            routeType={RouteTypes.Unprotected}
          />
          {/* TODO - The support portal is inactive. It's temporarily removed until a better solution is determined. */}
        </SubMenu>

        <div className={classes.navBarArrow}>
          {selectedNavBarIndex !== 0 && selectedNavBarIndex !== MenuCategory.AccountSettings ? (
            <IconButton
              size="large"
              className={classes.arrowButton}
              onClick={() => openSubMenu(selectedNavBarIndex)}
            >
              {!subMenuOpen ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          ) : null}
        </div>
        {navBarVisible ? null : (
          <div className={classes.navHamburger}>
            <IconButton size="large" className={classes.arrowButton} onClick={() => toggleNavBar()}>
              <Menu />
            </IconButton>
          </div>
        )}
      </div>
    </>
  );
}
